import React, {useState} from 'react'
import PropTypes from 'prop-types'
import {Input} from 'antd'
import SystemLogger from '../../SystemLogger'
import {FUNCTIONS_URL} from '../../config'
import {auth} from '../../Auth'

const buttonClasses = 'flex-row color-white bold align-center justify-center padding-1 margin-0-5'

const styles = {
  form: {
    padding: '2rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.8rem',
    backgroundColor: 'white',
  },
  label: {fontSize: '110%', fontWeight: 'bold', minWidth: '7rem'},
  row: {display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '0.5rem'},
  input: {flex: 1, borderRadius: '0.5rem'},
  button: {
    textDecoration: 'none',
    fontWeight: 'bold',
    fontSize: '110%',
    backgroundColor: 'cornflowerblue',
    border: 'none',
    cursor: 'pointer',
  },
  cancelButton: {
    textDecoration: 'none',
    fontWeight: 'bold',
    fontSize: '110%',
    backgroundColor: 'grey',
    border: 'none',
    cursor: 'pointer',
  },
  success: {
    textAlign: 'center',
    padding: '2rem',
    color: 'green',
    fontSize: '120%',
  },
  error: {
    color: 'red',
    fontSize: '90%',
    marginTop: '-0.4rem',
    paddingLeft: '7.5rem',
  },
  title: {textAlign: 'center', marginBottom: '1rem'},
  divider: {borderTop: '1px solid #eee', margin: '0.5rem 0'},
}

const REQUIRED_FIELDS = ['email', 'password', 'confirmPassword', 'name', 'phone', 'company', 'address']
const FIELD_LABELS = {
  email: 'Email',
  password: 'Password',
  confirmPassword: 'Confirm',
  name: 'Full Name',
  phone: 'Phone',
  company: 'Company',
  address: 'Address',
}

export default function RegisterClient({onSuccess, onCancel}) {
  const [fields, setFields] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
    phone: '',
    company: '',
    address: '',
  })
  const [fieldErrors, setFieldErrors] = useState({})
  const [submitting, setSubmitting] = useState(false)

  function setField(key) {
    return (e) => {
      setFields((prev) => ({...prev, [key]: e.target.value}))
      setFieldErrors((prev) => ({...prev, [key]: null}))
    }
  }

  function validate() {
    const errors = {}
    for (const key of REQUIRED_FIELDS) {
      if (!fields[key] || !fields[key].trim()) {
        errors[key] = `${FIELD_LABELS[key]} is required`
      }
    }
    if (!errors.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(fields.email)) {
      errors.email = 'Please enter a valid email address'
    }
    if (!errors.password && fields.password.length < 6) {
      errors.password = 'Password must be at least 6 characters'
    }
    if (!errors.confirmPassword && fields.password !== fields.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match'
    }
    return errors
  }

  async function submit(e) {
    e.preventDefault()
    const errors = validate()
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors)
      return
    }

    setSubmitting(true)
    const logger = new SystemLogger({})

    console.log('calling this api')

    try {
      const token = await auth.getToken()
      const headers = { 
        'Content-Type': `application/json` 
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const res = await fetch(`${FUNCTIONS_URL}/utilities/create_client`, {
        method: 'POST',
        mode: 'cors',
        headers,
        body: JSON.stringify({
          email: fields.email,
          password: fields.password,
          name: fields.name,
          phone: fields.phone,
          company: fields.company,
          address: fields.address,
          createdBy: {
            id: auth.sunkaizenUser.id,
            name: auth.sunkaizenUser.name,
          },
        }),
      })

      console.log('called api result',res)

      if (!res.ok) {
        const {error} = await res.json()
        setSubmitting(false)
        logger.error(error || 'Registration failed.')
        return
      }
    } catch (err) {
      setSubmitting(false)
      logger.error(err.message)
      return
    }

    setSubmitting(false)
    logger.event('Client account created successfully.')
    onSuccess()
  }

  return (
    <form onSubmit={submit} style={styles.form} data-testid="register-client-form">
      <h2 style={styles.title}>Add A Client</h2>

      <div style={styles.row}>
        <div style={styles.label}>Email:</div>
        <Input
          style={styles.input}
          type="text"
          value={fields.email}
          onChange={setField('email')}
          autoComplete="email"
        />
      </div>
      {fieldErrors.email && <div style={styles.error}>{fieldErrors.email}</div>}

      <div style={styles.row}>
        <div style={styles.label}>Password:</div>
        <Input.Password
          style={styles.input}
          value={fields.password}
          onChange={setField('password')}
          autoComplete="new-password"
        />
      </div>
      {fieldErrors.password && <div style={styles.error}>{fieldErrors.password}</div>}

      <div style={styles.row}>
        <div style={styles.label}>Confirm:</div>
        <Input.Password
          style={styles.input}
          value={fields.confirmPassword}
          onChange={setField('confirmPassword')}
          autoComplete="new-password"
        />
      </div>
      {fieldErrors.confirmPassword && <div style={styles.error}>{fieldErrors.confirmPassword}</div>}

      <div style={styles.divider} />

      <div style={styles.row}>
        <div style={styles.label}>Full Name:</div>
        <Input style={styles.input} value={fields.name} onChange={setField('name')} />
      </div>
      {fieldErrors.name && <div style={styles.error}>{fieldErrors.name}</div>}

      <div style={styles.row}>
        <div style={styles.label}>Phone:</div>
        <Input style={styles.input} value={fields.phone} onChange={setField('phone')} />
      </div>
      {fieldErrors.phone && <div style={styles.error}>{fieldErrors.phone}</div>}

      <div style={styles.row}>
        <div style={styles.label}>Company:</div>
        <Input style={styles.input} value={fields.company} onChange={setField('company')} />
      </div>
      {fieldErrors.company && <div style={styles.error}>{fieldErrors.company}</div>}

      <div style={styles.row}>
        <div style={styles.label}>Address:</div>
        <Input style={styles.input} value={fields.address} onChange={setField('address')} />
      </div>
      {fieldErrors.address && <div style={styles.error}>{fieldErrors.address}</div>}

      <div className="flex-row justify-space-evenly" style={{marginTop: '1rem'}}>
        <button
          className={buttonClasses}
          style={styles.button}
          type="submit"
          disabled={submitting}
        >
          {submitting ? 'Registering...' : 'Register'}
        </button>
        <button
          className={buttonClasses}
          style={styles.cancelButton}
          type="button"
          onClick={onCancel}
        >
          Cancel
        </button>
      </div>
    </form>
  )
}

RegisterClient.propTypes = {
  onSuccess: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
}
